import accounting from 'accounting';
import {
    defaultLineChartOptions, defaultGoalOptions, defaultDurationOptions,
} from './constants';
import { tooltipPositioner } from '../../../utils/tooltip';

export const getLineChartOptions = (
    data: any[],
    dateFormat: any,
    valueFormat: any,
    goal: number[],
    projection: boolean,
    tooltipFormat: (y: number, x: number) => string,
    xAxisFormat: any,
    yAxisFormat: ((this: any) => string) | null,
    height: number,
    xMin: number,
    ySoftMin: number,
    xTitle: string,
    yTitle: string,
    legendEnabled: boolean,
    lineChartLegendOptions: any,
    lineChartTooltipOptions: any,
    customXAxisFormat: ((value: any) => string) | null,
    isCustomClassRequired: boolean
) => {
    function formatter(this: { point: { y: number; x: number } }) {
        return `${tooltipFormat(this.point.y, this.point.x)}`;
    }

    function xAxisFormatter(this: any) {
        if (!this) return null;

        return customXAxisFormat?.(this) ?? null;
    }

    function positioner(this: { chart: any }, labelWidth: number, labelHeight: number, point: any) {
        return tooltipPositioner({
            labelWidth, labelHeight, point, chart: this.chart, yTitle,
        });
    }

    const getDateTimeLabelFormats = () => xAxisFormat || dateFormat;

    function getValueFormat(this: { value: number }) {
        const {
            symbol,
            precision,
            thousand,
            decimal,
            format,
        } = valueFormat;

        return accounting.formatMoney(this.value, symbol, precision, thousand, decimal, format);
    }

    let series: any[] = data.map(({ name, color, data, type }) => ({
        type: type || projection ? 'line' : 'area',
        name,
        data,
        color: color==='recommended' ? '#428351' : (color==='selected'? '#2167AE': color)
    }));

    if (goal.length > 0) {
        const goalOptions = { ...defaultGoalOptions, data: [goal] };
        const durationOptions = {
            ...defaultDurationOptions,
            data: [[-Infinity, 0], [goal[0], 0]],
        };

        series = [...series, { ...goalOptions }, { ...durationOptions }];
    }

    return {
        ...defaultLineChartOptions,
        chart: {
            ...defaultLineChartOptions.chart,
            height,
            marginTop: yTitle !== undefined && yTitle !== '' ? 30 : 0,
            styledMode: isCustomClassRequired
        },
        colors: ['#91C8F2', '#DAD2BD'],
        xAxis: {
            ...defaultLineChartOptions.xAxis,
            title: { text: xTitle },
            dateTimeLabelFormats: getDateTimeLabelFormats(),
            min: xMin,
            labels: {
                formatter: customXAxisFormat ? xAxisFormatter : undefined,
            },
        },
        yAxis: {
            ...defaultLineChartOptions.yAxis,
            title: { text: yTitle },
            labels: { formatter: yAxisFormat !== null ? yAxisFormat : getValueFormat },
            softMin: ySoftMin,
        },
        legend: { ...(lineChartLegendOptions ? lineChartLegendOptions : defaultLineChartOptions.legend), enabled: legendEnabled },
        tooltip: { ...(lineChartTooltipOptions ? lineChartTooltipOptions : defaultLineChartOptions.tooltip), formatter, positioner },
        series: [...series],
        lang: {
            sanitize: function (html: string) {
                return html.replace(
                    /\b(href|src)=["']?(javascript|data|vbscript):/gi,
                    '$1="#"'
                );
            }
        }
    };
};
